/**
 * Лид клиента — плашкой в ленте «Чатов» по времени создания.
 *
 * Просьба владельца 04.11.26: в хронологии видна рассылка «Лид выбрать время», но не видно, что ей
 * предшествовало, — «какой это именно лид, какой виджет заполнен, какой тип лида, чтобы было понятно,
 * какое действие совершил». Шапка — «Создан лид N» (ссылка на карточку лида) и тип; ниже — что
 * клиент заполнил на сайте (виджеты), реклама, салон, мероприятие, ответственный и статус сейчас;
 * последней строкой — какие рассылки ушли по этому лиду. Клиент плашку не видит. Данные — `leads`
 * ручек `GET /api/chats/{id}` и `…/history` (`chat/leads.py`).
 */
import { Globe, Mail, MessageCircleQuestion, MessagesSquare, Megaphone, Phone, PhoneIncoming, UserPlus } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router";

import type { ChatLead } from "@/lib/clientChats";

const hhmm = (iso: string) => iso.slice(11, 16);
const ddmm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

const ICON: Record<string, LucideIcon> = {
  popup: Globe,
  banner: Globe,
  saitorder: Globe,
  site: Globe,
  callback: PhoneIncoming,
  call: Phone,
  ask: MessageCircleQuestion,
  form: Globe,
  openline: MessagesSquare,
  email: Mail,
};

const MAILING: Record<string, string> = { wait: "ждёт отправки", sent: "ушла", error: "не ушла", cancel: "снята" };

export function LeadBubble({ l }: { l: ChatLead }) {
  const Icon = ICON[l.kind] ?? UserPlus;
  const facts = [
    l.ad && { k: "Реклама", v: l.ad },
    l.found && !l.ad && { k: "Откуда узнали", v: l.found },
    l.salon && { k: "Салон", v: l.salon },
    l.event && { k: "Мероприятие", v: l.event_date ? `${l.event} · ${ddmm(l.event_date)}` : l.event },
  ].filter(Boolean) as { k: string; v: string }[];
  return (
    <div
      className="mx-auto my-1 w-full max-w-[460px] rounded-[10px] border border-dashed border-border bg-bg-subtle px-3 py-2 text-[12.5px]"
      data-annot={`лид ${l.id} в ленте`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-fg-muted">
        <UserPlus size={13} className="text-accent-text" aria-hidden />
        <Link to={`/leads/${l.id}`} className="font-medium text-fg hover:underline" title="Карточка лида">
          Создан лид {l.id}
        </Link>
        {l.returning && <span title="Bitrix: клиент уже обращался раньше">· повторный клиент</span>}
        <span>· клиент не видит</span>
        <span className="ml-auto tabular-nums">{hhmm(l.date)}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1 rounded bg-accent/10 px-1.5 py-0.5 text-[11.5px] font-medium text-accent-text">
          <Icon size={12} aria-hidden />
          {l.kind_label}
        </span>
        {l.actions.map((a) => (
          <span key={a} className="rounded border border-border px-1.5 py-px text-[11.5px]" title="Что клиент заполнил на сайте">
            {a}
          </span>
        ))}
        {l.actions.length === 0 && !l.detail && <span className="text-fg-muted">{l.title}</span>}
      </div>
      {l.detail && <div className="mt-1 text-[12px] text-fg-muted">{l.detail}</div>}
      {facts.length > 0 && (
        <div className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 text-[12px]">
          {facts.map((f) => (
            <div key={f.k} className="contents">
              <span className="text-fg-muted">{f.k}</span>
              <span className="min-w-0 break-words">{f.v}</span>
            </div>
          ))}
        </div>
      )}
      {l.comment && <div className="mt-1 line-clamp-3 whitespace-pre-wrap text-[12px]">{l.comment}</div>}
      <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[11.5px] text-fg-muted">
        <span>
          Сейчас: <span className="text-fg">{l.status}</span>
        </span>
        <span>· {l.user ?? "не распределён"}</span>
      </div>
      {l.mailings.map((m, i) => (
        <div key={`${m.kind}-${i}`} className="mt-1 flex items-center gap-1 text-[12px]" title={m.error ?? undefined}>
          <Megaphone size={12} className="shrink-0 text-fg-faint" aria-hidden />
          <span>
            Рассылка «{m.title}» — {MAILING[m.status] ?? m.status}
            {m.date ? ` ${ddmm(m.date)} ${hhmm(m.date)}` : ""}
          </span>
        </div>
      ))}
    </div>
  );
}
